<template>
  <Dialog
    v-model:visible="visible"
    :style="{width: '80vw'}"
    :modal="true"
    :closable="!loading"
    :header="tituloModal"
    class="p-fluid mhv-dialog"
    @hide="resetModal"
  >

    <!-- ══ PASO 1 — Formulario ══ -->
    <div v-if="paso === 1" class="mhv-body">

      <!-- Banner en proceso -->
      <div v-if="enProceso" class="mhv-en-proceso">
        <div class="mhv-en-proceso-header">
          <div class="mhv-en-proceso-icon"><i class="pi pi-spin pi-spinner"></i></div>
          <div>
            <div class="mhv-en-proceso-titulo">Procesando hoja de vida</div>
            <div class="mhv-en-proceso-sub">
              {{ sincronizacion?.mensaje || 'La hoja de vida está siendo generada. Esto puede tardar entre 1 y 3 minutos.' }}
            </div>
          </div>
        </div>
        <ProgressBar mode="indeterminate" style="height:4px;border-radius:2px;margin-top:1rem"/>
        <div class="mhv-en-proceso-info">
          <i class="pi pi-info-circle mr-2"></i>
          No cierres esta ventana. Los datos se cargarán automáticamente cuando estén listos.
        </div>
      </div>

      <div class="mhv-intro" :class="modoResincronizar ? 'mhv-intro--warn' : ''">
        <i :class="modoResincronizar ? 'pi pi-refresh' : 'pi pi-truck'" class="mhv-intro-icon"></i>
        <div>
          <strong>{{ modoResincronizar ? 'Resincronización de Hoja de Vida' : 'Consulta de Hoja de Vida' }}</strong>
          <p v-if="modoResincronizar">
            Confirma o actualiza el documento del propietario para re-consultar la información oficial del vehículo
            <strong>{{ placaInicial }}</strong>.
          </p>
          <p v-else>
            Ingresa la placa y el documento del propietario para consultar la información oficial del vehículo.
          </p>
        </div>
      </div>

      <div class="mhv-form-grid mt-3">

        <div class="mhv-form-field">
          <label>Placa <span class="mhv-required">*</span></label>
          <InputText
            v-model="frm.placa"
            placeholder="Ej: ABC123"
            :class="{ 'p-invalid': errors.placa }"
            :disabled="modoResincronizar"
            @input="normalizarPlaca"
            maxlength="7"
          />
          <small class="mhv-error" v-if="errors.placa">{{ errors.placa }}</small>
          <small v-else-if="modoResincronizar" class="mhv-hint-lock">
            <i class="pi pi-lock mr-1"></i>Placa fijada desde el registro existente
          </small>
          <small v-else class="mhv-hint-gray">Formato colombiano: ABC123, YCS78D</small>
        </div>

        <div class="mhv-form-field">
          <label>Tipo de Documento <span class="mhv-required">*</span></label>
          <Dropdown
            v-model="frm.tipo_documento"
            :options="tiposDocumento"
            optionLabel="label" optionValue="value"
            placeholder="Seleccionar"
            :class="{ 'p-invalid': errors.tipo_documento }"
          />
          <small class="mhv-error" v-if="errors.tipo_documento">{{ errors.tipo_documento }}</small>
        </div>

        <div class="mhv-form-field">
          <label>Documento Propietario <span class="mhv-required">*</span></label>
          <InputText
            v-model="frm.documento_propietario"
            placeholder="Número de documento"
            :class="{ 'p-invalid': errors.documento }"
          />
          <small class="mhv-error" v-if="errors.documento">{{ errors.documento }}</small>
        </div>

      </div>

      <!-- Loading -->
      <div v-if="loading" class="mhv-loading mt-3">
        <ProgressBar mode="indeterminate" style="height:3px;border-radius:2px"/>
        <div class="mhv-loading-text">
          <i class="pi pi-spin pi-spinner mr-2"></i>
          {{ modoResincronizar ? 'Re-consultando información del vehículo…' : 'Consultando RUNT y SIMIT…' }}
        </div>
      </div>
    </div>

    <!-- ══ PASO 2 — Vista previa ══ -->
    <div v-if="paso === 2 && apiResponse" class="mhv-body">

      <!-- Header vehículo -->
      <div class="mhv-vehiculo-header mb-3">
        <div class="mhv-vehiculo-icon"><i class="pi pi-truck"></i></div>
        <div class="mhv-vehiculo-main">
          <div class="mhv-placa-badge">{{ vehiculo.placa }}</div>
          <div class="mhv-vehiculo-nombre">
            {{ vehiculo.marca }} {{ vehiculo.linea }}
            <span class="mhv-modelo-pill">{{ vehiculo.modelo }}</span>
          </div>
          <div class="mhv-vehiculo-sub">
            {{ vehiculo.clase_vehiculo }} · {{ vehiculo.tipo_servicio }} · {{ vehiculo.tipo_combustible }}
          </div>
        </div>
        <div class="mhv-vehiculo-badges">
          <div class="mhv-badge-item">
            <span class="mhv-badge-lbl">SOAT</span>
            <span :class="['mhv-status', estadoSoat.cls]">{{ estadoSoat.label }}</span>
            <span class="mhv-badge-fecha">{{ estadoSoat.vence }}</span>
          </div>
          <div class="mhv-badge-item">
            <span class="mhv-badge-lbl">RTM</span>
            <span :class="['mhv-status', estadoRtm.cls]">{{ estadoRtm.label }}</span>
            <span class="mhv-badge-fecha">{{ estadoRtm.vence }}</span>
          </div>
          <div class="mhv-badge-item">
            <span class="mhv-badge-lbl">Comparendos</span>
            <span :class="['mhv-count', totalComparendos===0?'mhv-count--ok':'mhv-count--danger']">
              {{ totalComparendos === 0 ? 'Sin deuda' : totalComparendos }}
            </span>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <TabView>

        <!-- Datos del Vehículo -->
        <TabPanel>
          <template #header><i class="pi pi-info-circle mr-2"></i>Datos del Vehículo</template>
          <div class="mhv-seccion-grid">

            <div class="mhv-seccion">
              <div class="mhv-seccion-titulo"><i class="pi pi-id-card"></i> Identificación</div>
              <div class="mhv-kv-grid">
                <span class="mhv-kv-lbl">Placa</span>       <span><span class="mhv-placa-sm">{{ vehiculo.placa }}</span></span>
                <span class="mhv-kv-lbl">Marca</span>       <span class="mhv-bold">{{ vehiculo.marca || '—' }}</span>
                <span class="mhv-kv-lbl">Línea</span>       <span>{{ vehiculo.linea || '—' }}</span>
                <span class="mhv-kv-lbl">Modelo</span>      <span>{{ vehiculo.modelo || '—' }}</span>
                <span class="mhv-kv-lbl">Color</span>       <span>{{ vehiculo.color || '—' }}</span>
                <span class="mhv-kv-lbl">Clase</span>       <span>{{ vehiculo.clase_vehiculo || '—' }}</span>
                <span class="mhv-kv-lbl">Tipo servicio</span><span>{{ vehiculo.tipo_servicio || '—' }}</span>
                <span class="mhv-kv-lbl">Combustible</span> <span>{{ vehiculo.tipo_combustible || '—' }}</span>
                <span class="mhv-kv-lbl">Carrocería</span>  <span>{{ vehiculo.tipo_carroceria || '—' }}</span>
                <span class="mhv-kv-lbl">Estado</span>
                <span><span :class="['mhv-status', vehiculo.estado_vehiculo==='ACTIVO'?'mhv-status--ok':'mhv-status--warn']">{{ vehiculo.estado_vehiculo || '—' }}</span></span>
              </div>
            </div>

            <div class="mhv-seccion">
              <div class="mhv-seccion-titulo"><i class="pi pi-cog"></i> Datos Técnicos</div>
              <div class="mhv-kv-grid">
                <span class="mhv-kv-lbl">Cilindraje</span>   <span>{{ vehiculo.cilindraje ? vehiculo.cilindraje+' cc' : '—' }}</span>
                <span class="mhv-kv-lbl">Núm. ejes</span>    <span>{{ vehiculo.num_ejes ?? '—' }}</span>
                <span class="mhv-kv-lbl">Cap. carga</span>   <span>{{ vehiculo.capacidad_carga ?? '—' }}</span>
                <span class="mhv-kv-lbl">Peso bruto</span>   <span>{{ vehiculo.peso_bruto_vehicular ?? '—' }}</span>
                <span class="mhv-kv-lbl">Pasajeros</span>    <span>{{ vehiculo.capacidad_pasajeros ?? '—' }}</span>
                <span class="mhv-kv-lbl">N° Motor</span>     <span class="mhv-mono">{{ vehiculo.numero_motor || '—' }}</span>
                <span class="mhv-kv-lbl">N° Chasis</span>    <span class="mhv-mono">{{ vehiculo.numero_chasis || '—' }}</span>
                <span class="mhv-kv-lbl">N° VIN</span>       <span class="mhv-mono">{{ vehiculo.numero_vin || '—' }}</span>
                <span class="mhv-kv-lbl">N° Serie</span>     <span class="mhv-mono">{{ vehiculo.numero_serie || '—' }}</span>
              </div>
            </div>

            <div class="mhv-seccion">
              <div class="mhv-seccion-titulo"><i class="pi pi-book"></i> Registro</div>
              <div class="mhv-kv-grid">
                <span class="mhv-kv-lbl">Nro. Licencia</span>    <span class="mhv-mono">{{ vehiculo.nro_licencia_transito || '—' }}</span>
                <span class="mhv-kv-lbl">Autoridad</span>        <span>{{ vehiculo.autoridad_transito || '—' }}</span>
                <span class="mhv-kv-lbl">Fecha matrícula</span>  <span>{{ fmtFecha(vehiculo.fecha_matricula) }}</span>
                <span class="mhv-kv-lbl">Fecha consulta</span>   <span>{{ fmtFechaHora(vehiculo.fecha_consulta) }}</span>
                <span class="mhv-kv-lbl">Doc. propietario</span> <span class="mhv-mono">{{ vehiculo.nro_doc || '—' }}</span>
                <span class="mhv-kv-lbl">Blindado</span>
                <span><span :class="['mhv-status', vehiculo.blindado?'mhv-status--warn':'mhv-status--sec']">{{ vehiculo.blindado?'Sí':'No' }}</span></span>
                <span class="mhv-kv-lbl">Repotenciado</span>
                <span><span :class="['mhv-status', vehiculo.repotenciado?'mhv-status--warn':'mhv-status--sec']">{{ vehiculo.repotenciado?'Sí':'No' }}</span></span>
                <span class="mhv-kv-lbl">Gravámenes</span>
                <span><span :class="['mhv-status', vehiculo.gravamenes_propiedad?'mhv-status--danger':'mhv-status--ok']">{{ vehiculo.gravamenes_propiedad?'Con gravamen':'Sin gravamen' }}</span></span>
                <span class="mhv-kv-lbl">Clásico</span>
                <span><span :class="['mhv-status', vehiculo.clasico_o_antiguo?'mhv-status--info':'mhv-status--sec']">{{ vehiculo.clasico_o_antiguo?'Sí':'No' }}</span></span>
              </div>
            </div>

          </div>
        </TabPanel>

        <!-- SOAT -->
        <TabPanel>
          <template #header><i class="pi pi-shield mr-2"></i>SOAT</template>
          <div v-if="!soatList.length" class="mhv-empty-tab"><i class="pi pi-times-circle"></i> Sin registros SOAT</div>
          <div v-for="(s,i) in soatList" :key="i" class="mhv-poliza-card" :class="s.estado==='VIGENTE'?'mhv-poliza-vigente':''">
            <div class="mhv-poliza-header">
              <span class="mhv-poliza-entidad">{{ s.entidad }}</span>
              <span :class="['mhv-status', s.estado==='VIGENTE'?'mhv-status--ok':'mhv-status--sec']">{{ s.estado }}</span>
            </div>
            <div class="mhv-poliza-kv">
              <div><span class="mhv-kv-lbl">N° Póliza</span>      <span class="mhv-mono">{{ s.numero_poliza }}</span></div>
              <div><span class="mhv-kv-lbl">Expedición</span>     <span>{{ fmtFecha(s.fecha_expedicion) }}</span></div>
              <div><span class="mhv-kv-lbl">Inicio vigencia</span><span>{{ fmtFecha(s.fecha_inicio_vigencia) }}</span></div>
              <div>
                <span class="mhv-kv-lbl">Fin vigencia</span>
                <span :class="s.estado==='VIGENTE'?'mhv-fecha-ok':'mhv-fecha-vencida'">{{ fmtFecha(s.fecha_fin_vigencia) }}</span>
              </div>
              <div><span class="mhv-kv-lbl">Código tarifa</span>  <span>{{ s.codigo_tarifa }}</span></div>
            </div>
          </div>
        </TabPanel>

        <!-- RTM -->
        <TabPanel>
          <template #header><i class="pi pi-wrench mr-2"></i>RTM / Tecnomecánica</template>
          <div v-if="!rtmList.length" class="mhv-empty-tab"><i class="pi pi-times-circle"></i> Sin registros RTM</div>
          <div v-for="(r,i) in rtmList" :key="i" class="mhv-poliza-card" :class="r.vigente?'mhv-poliza-vigente':''">
            <div class="mhv-poliza-header">
              <span class="mhv-poliza-entidad">{{ r.cda }}</span>
              <span :class="['mhv-status', r.vigente?'mhv-status--ok':'mhv-status--sec']">{{ r.vigente?'VIGENTE':'VENCIDA' }}</span>
            </div>
            <div class="mhv-poliza-kv">
              <div><span class="mhv-kv-lbl">N° Certificado</span><span class="mhv-mono">{{ r.nro_certificado }}</span></div>
              <div><span class="mhv-kv-lbl">Tipo revisión</span> <span>{{ r.tipo_revision }}</span></div>
              <div><span class="mhv-kv-lbl">Expedición</span>    <span>{{ fmtFecha(r.fecha_expedicion) }}</span></div>
              <div>
                <span class="mhv-kv-lbl">Vigencia hasta</span>
                <span :class="r.vigente?'mhv-fecha-ok':'mhv-fecha-vencida'">{{ fmtFecha(r.fecha_vigencia) }}</span>
              </div>
              <div>
                <span class="mhv-kv-lbl">Consistente</span>
                <span :class="['mhv-status', r.informacion_consistente?'mhv-status--ok':'mhv-status--danger']">
                  {{ r.informacion_consistente?'Sí':'No' }}
                </span>
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- Solicitudes RUNT -->
        <TabPanel>
          <template #header><i class="pi pi-list mr-2"></i>Solicitudes RUNT</template>
          <div v-if="!solicitudesList.length" class="mhv-empty-tab"><i class="pi pi-times-circle"></i> Sin solicitudes registradas</div>
          <table v-else class="mhv-tabla">
            <thead><tr>
              <th>N° Solicitud</th><th>Fecha</th><th>Entidad</th><th>Trámites</th><th>Estado</th>
            </tr></thead>
            <tbody>
              <tr v-for="(s,i) in solicitudesList" :key="i" :class="s.estado==='APROBADA'?'mhv-row-ok':''">
                <td><span class="mhv-mono">{{ s.nro_solicitud }}</span></td>
                <td>{{ fmtFecha(s.fecha_solicitud) }}</td>
                <td>{{ s.entidad }}</td>
                <td><span class="mhv-sub">{{ s.tramites }}</span></td>
                <td>
                  <span :class="['mhv-status', s.estado==='APROBADA'?'mhv-status--ok':s.estado==='AUTORIZADA'?'mhv-status--info':'mhv-status--warn']">
                    {{ s.estado }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </TabPanel>

        <!-- SIMIT -->
        <TabPanel>
          <template #header>
            <i class="pi pi-exclamation-triangle mr-2"></i>SIMIT
            <span v-if="totalComparendos > 0" class="mhv-tab-badge">{{ totalComparendos }}</span>
          </template>

          <div v-if="totalComparendos === 0" class="mhv-simit-clean">
            <i class="pi pi-check-circle mhv-simit-icon"></i>
            <strong>¡Sin comparendos!</strong>
            <p>{{ apiResponse.simitv?.estado_general }}</p>
          </div>

          <template v-else>
            <div class="mhv-deuda-resumen mb-3">
              <div class="mhv-deuda-item">
                <span class="mhv-kv-lbl">Comparendos</span>
                <span class="mhv-count mhv-count--danger">{{ totalComparendos }}</span>
              </div>
              <div class="mhv-deuda-item">
                <span class="mhv-kv-lbl">Multas</span>
                <span class="mhv-count mhv-count--warn">{{ apiResponse.simitv?.resumen?.multas ?? 0 }}</span>
              </div>
              <div class="mhv-deuda-item">
                <span class="mhv-kv-lbl">Total a pagar</span>
                <strong class="mhv-deuda-total">{{ fmtCOP(apiResponse.simitv?.resumen?.total_pagar) }}</strong>
                <!-- <strong class="nv-deuda-total ml-2">{{ fmtCOP(apiResponse.simitv?.resumen?.total_pagar) }}</strong> -->
              </div>
            </div>

            <table class="mhv-tabla">
              <thead><tr>
                <th style="width:110px">Fecha</th>
                <th style="width:80px">Tipo</th>
                <th style="width:70px">Cód.</th>
                <th>Secretaría</th>
                <th>Estado</th>
                <th style="width:110px">Valor</th>
                <th>N° Comparendo</th>
              </tr></thead>
              <tbody>
                <tr v-for="(c,i) in comparendosList" :key="i">
                  <td>{{ fmtFecha(c.fecha_imposicion) }}</td>
                  <td>
                    <span :class="['mhv-status', c.tipo==='multa'?'mhv-status--danger':'mhv-status--warn']">
                      {{ c.tipo?.toUpperCase() }}
                    </span>
                  </td>
                  <td><span class="mhv-mono mhv-bold">{{ c.infraccion?.substring(0,3) }}</span></td>
                  <td><span class="mhv-sub">{{ c.secretaria }}</span></td>
                  <td>
                    <span :class="['mhv-status', c.estado?.toLowerCase().includes('coactivo')?'mhv-status--danger':'mhv-status--warn']">
                      {{ c.estado }}
                    </span>
                  </td>
                  <td>
                    <span class="mhv-monto">{{ parseValorCOP(c.valor_cop) }}</span>
                  </td>
                  <td><span class="mhv-mono mhv-sub">{{ c.id_comparendo || '—' }}</span></td>
                </tr>
              </tbody>
            </table>
            <div class="mhv-nota mt-2">
              <i class="pi pi-info-circle"></i>
              Los valores individuales corresponden al acumulado del SIMIT. El total consolidado aparece en el resumen.
            </div>
          </template>
        </TabPanel>

        <!-- Fotos -->
        <TabPanel>
          <template #header>
            <i class="pi pi-images mr-2"></i>Fotos
            <span v-if="fotosVehiculoDocumento.length" class="mhv-tab-badge mhv-tab-badge--blue">
              {{ fotosVehiculoDocumento.length }}
            </span>
          </template>

          <div v-if="!fotosVehiculoDocumento.length" class="mhv-empty-tab">
            <i class="pi pi-times-circle"></i> Sin fotos registradas para este vehículo
          </div>

          <div v-else>
            <div class="mhv-fotos-doc-grid">
              <div
                v-for="foto in fotosVehiculoDocumento"
                :key="foto.key"
                class="mhv-foto-doc-card"
              >
                <!-- Header -->
                <div class="mhv-foto-doc-header">
                  <i :class="['pi', foto.icono]"></i>
                  <span>{{ foto.label }}</span>
                </div>

                <!-- Cargada -->
                <template v-if="foto.urlPublica">
                  <img :src="foto.urlPublica" :alt="foto.label" class="mhv-foto-doc-img" />
                  <a :href="foto.urlPublica" target="_blank" class="mhv-foto-doc-open">
                    <i class="pi pi-external-link mr-1"></i>Abrir en nueva pestaña
                  </a>
                </template>

                <!-- Cargando -->
                <div v-else-if="foto.loading" class="mhv-foto-doc-estado mhv-foto-doc-estado--loading">
                  <i class="pi pi-spin pi-spinner"></i>
                  <span>Cargando imagen…</span>
                </div>

                <!-- Error -->
                <div v-else-if="foto.error" class="mhv-foto-doc-estado mhv-foto-doc-estado--error">
                  <i class="pi pi-times-circle"></i>
                  <span>{{ foto.error }}</span>
                  <button class="mhv-btn-outline mhv-btn-sm"
                    @click.stop="verFotoVehiculo(foto.key, foto.urlEncriptada)">
                    <i class="pi pi-refresh mr-1"></i>Reintentar
                  </button>
                </div>

                <!-- Sin cargar — click para ver -->
                <div
                  v-else
                  class="mhv-foto-doc-estado mhv-foto-doc-estado--clickable"
                  @click="verFotoVehiculo(foto.key, foto.urlEncriptada)"
                >
                  <i class="pi pi-eye"></i>
                  <span>Clic para ver</span>
                  <small>Carga bajo demanda</small>
                </div>

              </div>
            </div>

            <div class="mhv-nota mt-3">
              <i class="pi pi-info-circle"></i>
              Las imágenes se cargan individualmente al hacer clic. No se almacenan localmente.
            </div>
          </div>
        </TabPanel>

      </TabView>
    </div>

    <!-- ══ Footer ══ -->
    <template #footer>
      <div class="mhv-footer">
        <button v-if="paso === 2" class="mhv-btn-outline" :disabled="loading" @click="paso = 1">
          <i class="pi pi-arrow-left mr-1"></i> Volver
        </button>
        <span v-else></span>

        <div class="mhv-footer-right">
          <button class="mhv-btn-outline" :disabled="loading" @click="cerrar">
            <i class="pi pi-times mr-1"></i> Cancelar
          </button>

          <button v-if="paso === 1" class="mhv-btn-action mhv-btn-action--navy"
            :class="modoResincronizar ? 'mhv-btn-action--warn' : 'mhv-btn-action--navy'"
            :disabled="loading || enProceso" @click="consultarVehiculo">
            <i :class="loading ? 'pi pi-spin pi-spinner' : modoResincronizar ? 'pi pi-refresh' : 'pi pi-search'" class="mr-1"></i>
            {{ modoResincronizar ? 'Resincronizar' : 'Consultar' }}
          </button>

          <button v-if="paso === 2 && !modoResincronizar" class="mhv-btn-action mhv-btn-action--navy"
            :disabled="loading" @click="guardarVehiculo">
            <i :class="loading ? 'pi pi-spin pi-spinner' : 'pi pi-save'" class="mr-1"></i>
            Guardar
          </button>

          <button v-if="paso === 2 && modoResincronizar" class="mhv-btn-action mhv-btn-action--ok"
            @click="$emit('guardado'); cerrar()">
            <i class="pi pi-check mr-1"></i> Cerrar
          </button>
        </div>
      </div>
    </template>

  </Dialog>
</template>

<script>
import hvPollingMixin from '../../../mixins/hvPollingMixin.js';

export default {
  name: 'ModalNuevoVehiculo',

  mixins: [hvPollingMixin],

  props: {
    modelValue:        { type: Boolean,          default: false },
    idRol:             { type: [Number, String], required: true },
    idCustomer:        { type: [Number, String, null], default: null },
    placaInicial:      { type: String,           default: '' },
    documentoInicial:  { type: String,           default: '' },
    modoResincronizar: { type: Boolean,          default: false },
  },

  emits: ['update:modelValue', 'guardado'],

  watch: {
    modelValue(val) {
      if (val && this.modoResincronizar) {
        this.frm.placa                  = this.placaInicial;
        this.frm.documento_propietario  = this.documentoInicial;
        this.frm.tipo_documento         = this.frm.tipo_documento ?? 1;
      }
    },
  },

  data() {
    return {
      paso:        1,
      loading:     false,
      apiResponse: null,
      enProceso:      false,
      sincronizacion: null,

      frm: {
        placa:                 '',
        tipo_documento:        null,
        documento_propietario: '',
      },

      fotosPreview: { frontal:null, lateral_der:null, lateral_izq:null, posterior:null, soat:null, tecnomecanica:null },
      errors: {},

      tiposDocumento: [
        { label:'C.C. — Cédula de Ciudadanía',           value:1 },
        { label:'C.E. — Cédula de Extranjería',           value:3 },
        { label:'NIT — Nit Empresa',                      value:5 },
        { label:'P.P. — Pasaporte',                       value:4 },
        { label:'T.I. — Tarjeta de Identidad',            value:2 },
        { label:'C.D. — Carnet Diplomático',              value:6 },
        { label:'R.C. — Registro Civil',                  value:7 },
        { label:'P.P.T — Permiso por Protección Temporal',value:8 },
      ],

      tiposFoto: [
        { key:'frontal',       label:'Frontal'       },
        { key:'lateral_der',   label:'Lateral Der.'  },
        { key:'lateral_izq',   label:'Lateral Izq.'  },
        { key:'posterior',     label:'Posterior'     },
        { key:'soat',          label:'SOAT'          },
        { key:'tecnomecanica', label:'Tecnomecánica' },
      ],
      fotosVehiculo: {
          Foto_vehiculo_frontal:      null,
          Foto_vehiculo_posterior:    null,
          Foto_vehiculo_lateral_der:  null,
          Foto_vehiculo_lateral_izq:  null,
          Foto_soat:                  null,
          Foto_tecnomecanica:         null,
      },
      fotosVehiculoLoading: {
          Foto_vehiculo_frontal:      false,
          Foto_vehiculo_posterior:    false,
          Foto_vehiculo_lateral_der:  false,
          Foto_vehiculo_lateral_izq:  false,
          Foto_soat:                  false,
          Foto_tecnomecanica:         false,
      },
      fotosVehiculoError: {},
    };
  },

  computed: {
    visible: {
      get()    { return this.modelValue; },
      set(val) { this.$emit('update:modelValue', val); },
    },

    tituloModal() {
      if (this.modoResincronizar) {
        return this.paso === 1
          ? `Resincronizar — ${this.placaInicial}`
          : `Hoja de Vida Actualizada — ${this.vehiculo?.placa ?? this.frm.placa}`;
      }
      return this.paso === 1
        ? 'Registrar Nuevo Vehículo'
        : `Hoja de Vida — ${this.vehiculo?.placa ?? this.frm.placa}`;
    },

    dataJson() {
      return this.apiResponse?.hoja_de_vida_vehiculo?.data_json
          ?? this.apiResponse?.data_json
          ?? this.apiResponse
          ?? null;
    },

    vehiculo()       { return this.dataJson?.runt?.vehiculo   ?? {}; },
    soatList()       { return this.dataJson?.runt?.soat        ?? []; },
    rtmList()        { return this.dataJson?.runt?.rtm         ?? []; },
    solicitudesList(){ return this.dataJson?.runt?.solicitudes ?? []; },
    comparendosList(){ return this.dataJson?.simitv?.comparendos ?? []; },
    totalComparendos(){ return this.comparendosList.length; },

    estadoSoat() {
      if (!this.soatList.length) return { label:'Sin datos', cls:'mhv-status--sec', vence:'' };
      const ultimo = this.soatList.reduce((a,b) =>
        new Date(a.fecha_fin_vigencia??0) > new Date(b.fecha_fin_vigencia??0) ? a : b);
      const v = ultimo.estado === 'VIGENTE';
      return { label: v?'VIGENTE':'VENCIDO', cls: v?'mhv-status--ok':'mhv-status--danger',
               vence: `Vence: ${this.fmtFecha(ultimo.fecha_fin_vigencia)}` };
    },

    estadoRtm() {
      if (!this.rtmList.length) return { label:'Sin datos', cls:'mhv-status--sec', vence:'' };
      const ultimo = this.rtmList.reduce((a,b) =>
        new Date(a.fecha_vigencia??0) > new Date(b.fecha_vigencia??0) ? a : b);
      return { label: ultimo.vigente?'VIGENTE':'VENCIDA', cls: ultimo.vigente?'mhv-status--ok':'mhv-status--danger',
               vence: `Vence: ${this.fmtFecha(ultimo.fecha_vigencia)}` };
    },
    fotosVehiculoDocumento() {
        // Las fotos vienen en el nivel raíz de hoja_de_vida_vehiculo
        const hv = this.apiResponse?.hoja_de_vida_vehiculo ?? this.apiResponse ?? null;
        if (!hv) return [];

        return [
            { key: 'Foto_vehiculo_frontal',     label: 'Frontal',        icono: 'pi-car'    },
            { key: 'Foto_vehiculo_posterior',   label: 'Posterior',      icono: 'pi-car'    },
            { key: 'Foto_vehiculo_lateral_der', label: 'Lateral Der.',   icono: 'pi-car'    },
            { key: 'Foto_vehiculo_lateral_izq', label: 'Lateral Izq.',   icono: 'pi-car'    },
            { key: 'Foto_soat',                 label: 'SOAT',           icono: 'pi-shield' },
            { key: 'Foto_tecnomecanica',        label: 'Tecnomecánica',  icono: 'pi-wrench' },
        ].map(f => ({
            ...f,
            urlEncriptada: hv[f.key] ?? null,
            urlPublica:    this.fotosVehiculo[f.key],
            loading:       this.fotosVehiculoLoading[f.key],
            error:         this.fotosVehiculoError[f.key] ?? null,
        })).filter(f => f.urlEncriptada);
    },
  },

  methods: {
    validarPaso1() {
      this.errors = {};
      const placa = this.frm.placa.toUpperCase().trim();
      const regex = /^[A-Z]{3}[0-9]{2}[A-Z0-9]{1}[0-9]?$/;
      if (!placa)                    this.errors.placa          = 'La placa es obligatoria.';
      else if (!regex.test(placa))   this.errors.placa          = 'Formato inválido. Ej: ABC123 o YCS77D.';
      if (!this.frm.tipo_documento)  this.errors.tipo_documento = 'Selecciona el tipo de documento.';
      if (!this.frm.documento_propietario) this.errors.documento = 'El documento es obligatorio.';
      return Object.keys(this.errors).length === 0;
    },

    normalizarPlaca(e) {
      this.frm.placa = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    },

    async consultarVehiculo() {
      if (!this.validarPaso1()) return;
      this.loading    = true;
      this.enProceso  = false;
      this.sincronizacion = null;
      try {
        const res = await window.api.call('post', '/registrar_vehiculo_hv', {
          id_rol:                this.idRol,
          id_customer:           this.idCustomer,
          placa:                 this.frm.placa,
          tipo_documento:        this.frm.tipo_documento,
          documento_propietario: this.frm.documento_propietario,
        });

        if (res.status === 200) {
          this.loading = false;
          const data = res.data;

          // ── Caso: hoja de vida en proceso de creación ──────────────
          if (data.en_proceso === true) {
            this.enProceso      = true;
            this.sincronizacion = data.sincronizacion ?? {};
            this.iniciarPollingSolicitudHv('vehicle', this.frm.placa);
            return; // No avanza al paso 2
          }

          // ── Caso: datos completos ───────────────────────────────────
          this.apiResponse = data?.data ?? data;
          this.paso = 2;
        }
      } catch (err) {
        this.loading = false;
        const status   = err.response?.status ?? err?.status ?? 0;
        const errorKey = err.response?.data?.error ?? err?.data?.error ?? '';
        const msg      = err.response?.data?.message ?? err?.data?.message
                      ?? err.response?.data?.error   ?? err?.data?.error
                      ?? 'No se pudo consultar el vehículo.';

        // ── Datos no corresponden al propietario ────────────────────────
        if (status === 422 || errorKey === 'datos_inconsistentes') {
          this.$toast.add({
            severity: 'warn',
            summary:  '⚠️ Datos no corresponden',
            detail:   msg,
            life:     10000,
          });
          return;
        }

        // ── Token expirado / revocado ───────────────────────────────────
        if (status === 401 || errorKey === 'token_expirado') {
          this.$toast.add({
            severity: 'error',
            summary:  'Servicio no disponible',
            detail:   msg,
            life:     12000,
          });
          return;
        }

        // ── Ya hay una solicitud en proceso para esta placa ─────────────
        if (status === 409 || errorKey === 'solicitud_en_proceso') {
          const body = err.response?.data ?? err?.data ?? {};
          this.enProceso      = true;
          this.sincronizacion = { mensaje: msg, consulta_id: body.consulta_id };
          this.iniciarPollingSolicitudHv('vehicle', this.frm.placa);
          return;
        }

        // ── Error genérico ──────────────────────────────────────────────
        this.$toast.add({
          severity: 'error',
          summary:  'Error de consulta',
          detail:   msg,
          life:     6000,
        });
      }
    },

    // ── Callbacks del hvPollingMixin ──────────────────────────────────────
    async onSolicitudHvCompletada() {
      this.enProceso = false;
      // Ya no hay bloqueo activo: reconsultamos una vez para traer los datos completos.
      await this.consultarVehiculo();
      if (this.paso === 2) {
        this.$toast.add({ severity:'success', summary:'Hoja de vida lista', detail:'Los datos del vehículo han sido cargados.', life:3000 });
      }
    },

    onSolicitudHvError(mensaje) {
      this.enProceso = false;
      this.$toast.add({
        severity: 'error',
        summary:  'Error al generar la hoja de vida',
        detail:   mensaje || 'No se pudo generar la hoja de vida. Intenta de nuevo.',
        life:     8000,
      });
    },

    onSolicitudHvTimeout() {
      this.enProceso = false;
      this.$toast.add({
        severity: 'warn',
        summary:  'Consulta demorada',
        detail:   'La consulta está tardando demasiado. Intenta de nuevo en unos minutos.',
        life:     8000,
      });
    },

    async guardarVehiculo() {
      this.loading = true;
      try {
        const res = await window.api.call('post', '/guardar_vehiculo_hv', {
          id_rol:                this.idRol,
          id_customer:           this.idCustomer,
          placa:                 this.frm.placa,
          tipo_documento:        this.frm.tipo_documento,
          documento_propietario: this.frm.documento_propietario,
          api_response:          this.apiResponse,
        });
        if (res.status === 200) {
          this.$toast.add({ severity:'success', summary:'Vehículo guardado', detail:`${this.frm.placa} registrado correctamente`, life:4000 });
          this.$emit('guardado');
          this.cerrar();
        }
      } catch (err) {
        this.$toast.add({ severity:'error', summary:'Error al guardar', detail: err?.data?.message ?? 'No se pudo guardar', life:5000 });
      } finally {
        this.loading = false;
      }
    },

    triggerFoto(key) {
      const ref = this.$refs[`fotoInput_${key}`];
      if (!ref) return;
      const el = Array.isArray(ref) ? ref[0] : ref;
      el.click();
    },

    onFotoSeleccionada(event, key) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => { this.fotosPreview[key] = e.target.result; };
      reader.readAsDataURL(file);
    },

    cerrar() { this.visible = false; },

    resetModal() {
      this.detenerPollingSolicitudHv();
      this.paso        = 1;
      this.loading     = false;
      this.apiResponse = null;
      this.enProceso      = false;
      this.sincronizacion = null;
      this.errors      = {};
      this.fotosPreview = { frontal:null, lateral_der:null, lateral_izq:null, posterior:null, soat:null, tecnomecanica:null };
      this.frm = { placa:'', tipo_documento:null, documento_propietario:'' };
      this.resetFotosVehiculo();
    },

    fmtFecha(f) {
      if (!f) return '—';
      return new Date(f + 'T00:00:00').toLocaleDateString('es-CO', { day:'2-digit', month:'short', year:'numeric' });
    },

    fmtFechaHora(f) {
      if (!f) return '—';
      return new Date(f).toLocaleString('es-CO', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
    },

    fmtCOP(valor) {
      if (!valor && valor !== 0) return '—';
      let v = typeof valor==='string' ? parseFloat(valor.replace(/\./g,'').replace(',','.')) : valor;
      if (isNaN(v)) return '—';
      return new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v);
    },
    parseValorCOP(valor) {
        if (!valor) return '$0';

        // Convertir a número
        let numero = Number(valor);

        // Si viene exageradamente grande
        // puedes dividirlo según la escala real
        numero = numero / 1000000;

        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(numero);
    },
    async verFotoVehiculo(key, urlEncriptada) {
        if (this.fotosVehiculo[key]) return;

        this.fotosVehiculoLoading[key] = true;
        this.fotosVehiculoError[key]   = null;

        const cookie = {};
        document.cookie.split(';').forEach(el => {
            const [k, v] = el.split('=');
            cookie[k.trim()] = decodeURIComponent(v ?? '');
        });

        try {
            const res = await window.api.call('post', '/img_public', {
                email:      cookie['Email'],
                id_rol:     this.idRol,
                imagenes:   [urlEncriptada],
                onlyimages: true,
            });

            const images   = res.data?.data?.images ?? [];
            const blob     = images[0]?.blob ?? null;

            if (blob) {
                this.fotosVehiculo[key] = blob;
            } else {
                this.fotosVehiculoError[key] = 'No se pudo obtener la imagen.';
            }
        } catch {
            this.fotosVehiculoError[key] = 'Error al cargar la imagen.';
        } finally {
            this.fotosVehiculoLoading[key] = false;
        }
    },

    resetFotosVehiculo() {
        this.fotosVehiculo = {
            Foto_vehiculo_frontal: null, Foto_vehiculo_posterior: null,
            Foto_vehiculo_lateral_der: null, Foto_vehiculo_lateral_izq: null,
            Foto_soat: null, Foto_tecnomecanica: null,
        };
        this.fotosVehiculoLoading = { ...this.fotosVehiculo };
        this.fotosVehiculoError   = {};
    },
  },
};
</script>

<style scoped lang="scss">
$navy:#0A354E; $white:#fff; $g200:#e8eaed; $g500:#6b7280; $g700:#374151; $r:12px;

/* Dialog */
.mhv-dialog {
  :deep(.p-dialog-header) { font-weight:700; font-size:1rem; border-bottom:1px solid $g200; }
  :deep(.p-dialog-content){ padding:1.5rem; }
  :deep(.p-dialog-footer) { border-top:1px solid $g200; padding:1rem 1.5rem; }
}
.mhv-body { display:flex; flex-direction:column; gap:1rem; }

/* Buttons */
.mhv-btn-outline { display:inline-flex; align-items:center; gap:.3rem; background:$white; color:$g700; border:1px solid $g200; border-radius:8px; padding:.45rem .9rem; font-size:.82rem; font-weight:600; cursor:pointer; transition:all .15s; &:hover:not(:disabled){border-color:$navy;color:$navy;} &:disabled{opacity:.6;cursor:not-allowed;} }
.mhv-btn-action  { display:inline-flex; align-items:center; gap:.3rem; border:none; border-radius:8px; padding:.45rem 1rem; font-size:.82rem; font-weight:600; cursor:pointer; transition:background .15s; &:disabled{opacity:.6;cursor:not-allowed;} }
.mhv-btn-action--navy { background:$navy; color:#fff; &:hover:not(:disabled){background:#0c4f77;} }
.mhv-btn-action--warn { background:#ea580c; color:#fff; &:hover:not(:disabled){background:#c2410c;} }
.mhv-btn-action--ok   { background:#16a34a; color:#fff; &:hover:not(:disabled){background:#15803d;} }

/* Footer */
.mhv-footer { display:flex; justify-content:space-between; align-items:center; width:100%; }
.mhv-footer-right { display:flex; gap:.65rem; }

/* ── Estado en proceso ── */
.mhv-en-proceso { border:1px solid #bfdbfe; border-radius:$r; padding:1.5rem; background:#eff6ff; }
.mhv-en-proceso-header { display:flex; align-items:flex-start; gap:1rem; }
.mhv-en-proceso-icon { width:48px; height:48px; border-radius:12px; background:#dbeafe; display:flex; align-items:center; justify-content:center; flex-shrink:0; i{font-size:1.5rem;color:#2563eb;} }
.mhv-en-proceso-titulo { font-size:1rem; font-weight:700; color:#1e40af; }
.mhv-en-proceso-sub    { font-size:.82rem; color:#3b82f6; margin-top:3px; }
.mhv-en-proceso-info { margin-top:.875rem; padding:.625rem .875rem; background:#dbeafe; border-radius:6px; font-size:.8rem; color:#1e40af; display:flex; align-items:center; }

/* ── Intro banner ── */
.mhv-intro { display:flex; align-items:flex-start; gap:1rem; background:#eff6ff; border:1px solid #bfdbfe; border-radius:8px; padding:1rem 1.25rem; p{margin:.25rem 0 0;font-size:.875rem;color:#475569;} }
.mhv-intro--warn { background:#fff7ed; border-color:#fed7aa; i{color:#ea580c!important;} }
.mhv-intro-icon { font-size:2rem; color:$navy; margin-top:2px; flex-shrink:0; }

/* ── Formulario paso 1 ── */
.mhv-form-grid  { display:grid; grid-template-columns:repeat(3,1fr); gap:.875rem; }
.mhv-form-field { display:flex; flex-direction:column; gap:.3rem; label{font-size:.72rem;font-weight:700;color:$g500;text-transform:uppercase;letter-spacing:.5px;} }
.mhv-required   { color:#ef4444; }
.mhv-error      { font-size:.72rem; color:#dc2626; }
.mhv-hint-lock  { font-size:.72rem; color:$navy; }
.mhv-hint-gray  { font-size:.72rem; color:#94a3b8; }
.mhv-loading    { }
.mhv-loading-text { margin-top:.75rem; font-size:.875rem; color:#475569; display:flex; align-items:center; }

/* ── Header vehículo paso 2 ── */
.mhv-vehiculo-header { display:flex; align-items:flex-start; gap:1.25rem; background:linear-gradient(135deg,#0A354E 0%,#0c4f77 100%); border-radius:$r; padding:1.25rem 1.5rem; color:white; flex-wrap:wrap; }
.mhv-vehiculo-icon   { width:56px; height:56px; background:rgba(255,255,255,.15); border-radius:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0; i{font-size:1.75rem;color:white;} }
.mhv-vehiculo-main   { flex:1; min-width:200px; }
.mhv-vehiculo-nombre { font-size:1.05rem; font-weight:700; margin-top:4px; display:flex; align-items:center; gap:.5rem; flex-wrap:wrap; }
.mhv-vehiculo-sub    { font-size:.76rem; color:rgba(255,255,255,.7); margin-top:3px; }
.mhv-placa-badge     { display:inline-block; background:white; color:$navy; font-size:1.1rem; font-weight:800; padding:3px 14px; border-radius:6px; letter-spacing:2px; }
.mhv-placa-sm        { background:$navy; color:white; padding:1px 8px; border-radius:4px; font-size:.8rem; font-weight:700; letter-spacing:1px; }
.mhv-modelo-pill     { background:rgba(255,255,255,.2); border-radius:20px; padding:1px 10px; font-size:.75rem; font-weight:500; }
.mhv-vehiculo-badges { display:flex; gap:1.25rem; flex-wrap:wrap; align-items:flex-start; }
.mhv-badge-item      { display:flex; flex-direction:column; align-items:center; gap:4px; }
.mhv-badge-lbl       { font-size:.65rem; color:rgba(255,255,255,.7); font-weight:600; text-transform:uppercase; }
.mhv-badge-fecha     { font-size:.65rem; color:rgba(255,255,255,.6); }

/* Status / count */
.mhv-status { display:inline-block; font-size:.7rem; font-weight:700; padding:.2rem .55rem; border-radius:20px; white-space:nowrap; }
.mhv-status--ok     { background:#f0fdf4; color:#16a34a; }
.mhv-status--danger { background:#fef2f2; color:#dc2626; }
.mhv-status--warn   { background:#fffbeb; color:#d97706; }
.mhv-status--sec    { background:#f1f5f9; color:#64748b; }
.mhv-status--info   { background:#eff6ff; color:#2563eb; }
.mhv-count { display:inline-flex; align-items:center; justify-content:center; min-width:24px; height:24px; padding:0 6px; border-radius:12px; font-size:.78rem; font-weight:700; }
.mhv-count--ok     { background:#f0fdf4; color:#16a34a; }
.mhv-count--danger { background:#fef2f2; color:#dc2626; }
.mhv-count--warn   { background:#fffbeb; color:#d97706; }

/* Secciones */
.mhv-seccion-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(250px,1fr)); gap:1rem; }
.mhv-seccion { border:1px solid #e5e7eb; border-radius:8px; padding:1rem; background:#fafbfc; }
.mhv-seccion-titulo { font-size:.72rem; font-weight:700; color:$navy; text-transform:uppercase; letter-spacing:.5px; margin-bottom:.75rem; display:flex; align-items:center; gap:6px; i{font-size:.78rem;} }
.mhv-kv-grid { display:grid; grid-template-columns:130px 1fr; gap:.35rem .5rem; font-size:.8rem; }
.mhv-kv-lbl  { font-weight:600; color:#64748b; }
.mhv-bold    { font-weight:700; }
.mhv-mono    { font-family:monospace; font-size:.79rem; color:$g700; }
.mhv-sub     { font-size:.75rem; color:$g500; }

/* Póliza cards */
.mhv-poliza-card { border:1px solid #e5e7eb; border-radius:8px; padding:1rem 1.25rem; margin-bottom:.75rem; background:#f9fafb; &.mhv-poliza-vigente{border-color:#86efac;background:#f0fdf4;} }
.mhv-poliza-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:.75rem; }
.mhv-poliza-entidad { font-weight:700; font-size:.88rem; color:$g700; }
.mhv-poliza-kv { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:.4rem 1rem; font-size:.8rem; div{display:flex;flex-direction:column;gap:1px;} }
.mhv-fecha-ok     { color:#16a34a; font-weight:700; }
.mhv-fecha-vencida{ color:#dc2626; }

/* Tabla (Solicitudes / SIMIT) */
.mhv-tabla { width:100%; border-collapse:collapse; font-size:.8rem; }
.mhv-tabla thead tr { background:#f8f9fa; }
.mhv-tabla th { text-align:left; padding:.55rem .75rem; font-size:.63rem; font-weight:700; letter-spacing:.7px; text-transform:uppercase; color:$g500; border-bottom:1px solid $g200; }
.mhv-tabla td { padding:.6rem .75rem; border-bottom:1px solid #f1f3f4; color:$g700; vertical-align:middle; }
.mhv-tabla tbody tr:last-child td { border-bottom:none; }
.mhv-tabla tbody tr:hover td { background:#fafbfc; }
.mhv-row-ok td { background:#f0fdf4 !important; }

/* SIMIT */
.mhv-simit-clean { display:flex; flex-direction:column; align-items:center; padding:2.5rem 1rem; text-align:center; gap:.5rem; strong{color:#166534;} p{color:#4b7a60;font-size:.875rem;margin:0;} }
.mhv-simit-icon  { font-size:2.5rem; color:#22c55e; }
.mhv-deuda-resumen { display:flex; justify-content:space-around; flex-wrap:wrap; gap:.5rem; padding:.875rem 1.25rem; background:#fef2f2; border:1px solid #fecaca; border-radius:8px; }
.mhv-deuda-item  { display:flex; flex-direction:column; align-items:center; gap:.3rem; }
.mhv-deuda-total { font-size:1rem; color:#dc2626; font-weight:700; }
.mhv-monto       { color:#dc2626; font-weight:600; font-size:.8rem; }
.mhv-tab-badge   { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; background:#dc2626; color:white; border-radius:50%; font-size:.65rem; font-weight:700; margin-left:.4rem; }
.mhv-nota        { font-size:.72rem; color:#94a3b8; display:flex; align-items:center; gap:.4rem; i{font-size:.72rem;} }
.mhv-empty-tab   { display:flex; align-items:center; gap:.5rem; padding:2rem 1rem; color:#94a3b8; font-size:.875rem; i{font-size:1.25rem;} }

/* Fotos */
.mhv-fotos-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:1rem; }
.mhv-foto-uploader { border:2px dashed #cbd5e1; border-radius:10px; cursor:pointer; overflow:hidden; transition:border-color .2s,box-shadow .2s; &:hover{border-color:$navy;box-shadow:0 2px 8px rgba(10,53,78,.12);} }
.mhv-foto-img { width:100%; height:140px; object-fit:cover; display:block; }
.mhv-foto-placeholder { height:140px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.4rem; color:#94a3b8; i{font-size:1.75rem;} span{font-size:.8rem;} small{font-size:.7rem;color:#cbd5e1;} }
.mhv-foto-label { text-align:center; font-size:.72rem; font-weight:600; color:#475569; padding:4px 0 6px; background:#f8fafc; }

/* Responsive */
@media(max-width:768px) {
  .mhv-form-grid { grid-template-columns:1fr; }
  .mhv-vehiculo-header { flex-direction:column; }
  .mhv-seccion-grid { grid-template-columns:1fr; }
  .mhv-kv-grid { grid-template-columns:110px 1fr; }
  .mhv-footer { flex-direction:column; gap:.75rem; align-items:stretch; .mhv-footer-right{justify-content:flex-end;} }
}
/* ── Fotos API (vehículo) ── */
.mhv-fotos-doc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 1rem;
}

.mhv-foto-doc-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0,0,0,.06);
  transition: box-shadow .2s;

  &:hover { box-shadow: 0 4px 12px rgba(10,53,78,.1); }
}

.mhv-foto-doc-header {
  display: flex;
  align-items: center;
  gap: .4rem;
  padding: .55rem .75rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: .72rem;
  font-weight: 700;
  color: $navy;

  i { font-size: .78rem; flex-shrink: 0; }
  span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
}

.mhv-foto-doc-img {
  width: 100%;
  height: 155px;
  object-fit: cover;
  display: block;
}

.mhv-foto-doc-open {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .3rem;
  padding: .4rem;
  font-size: .7rem;
  font-weight: 600;
  color: $navy;
  background: #eff6ff;
  border-top: 1px solid #bfdbfe;
  text-decoration: none;
  transition: background .15s;

  &:hover { background: #dbeafe; }
}

.mhv-foto-doc-estado {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .45rem;
  min-height: 155px;
  padding: 1rem;
  text-align: center;

  i    { font-size: 1.75rem; }
  span { font-size: .8rem; }
  small { font-size: .7rem; color: #cbd5e1; }

  &--clickable {
    cursor: pointer;
    color: #94a3b8;
    transition: background .15s, color .15s;

    &:hover {
      background: #eff6ff;
      color: $navy;
      i { color: $navy; }
    }
  }

  &--loading {
    color: #3b82f6;
    i { color: #3b82f6; }
  }

  &--error {
    color: #dc2626;
    gap: .6rem;
    i { font-size: 1.4rem; color: #dc2626; }
  }
}

.mhv-tab-badge--blue { background: #2563eb !important; }
.mhv-btn-sm { padding: .28rem .65rem; font-size: .73rem; }
</style>